// Estatísticas do diário (Daylio): funções puras para agregação.
// Mantidas separadas da UI para serem testáveis sem React.
//
// O agrupamento de humores (e cores) espelha o getMoodColor original do App:
//   rad (melhor) > good > ok/meh > bad/sick/shitty day > awful/sensitive (pior).
// Daí derivamos uma pontuação 5..1 para médias — em vez de "chutar" uma ordem.

export const MOOD_GROUPS = [
  { score: 5, color: '#f39c12', moods: ['rad'] },                       // laranja
  { score: 4, color: '#2ecc71', moods: ['good'] },                      // verde
  { score: 3, color: '#9b59b6', moods: ['ok', 'meh'] },                 // roxo
  { score: 2, color: '#3498db', moods: ['bad', 'sick', 'shitty day'] }, // azul
  { score: 1, color: '#95a5a6', moods: ['awful', 'sensitive'] },        // cinza
];

const FALLBACK_COLOR = '#f39c12';

const normalizeMood = (mood) => (mood || '').trim().toLowerCase();

const MOOD_LOOKUP = MOOD_GROUPS.reduce((acc, group) => {
  group.moods.forEach((mood) => {
    acc[mood] = group;
  });
  return acc;
}, {});

// Ordem canônica (melhor -> pior) para eixos de gráficos.
export const MOOD_ORDER = MOOD_GROUPS.flatMap((group) => group.moods);

export const getMoodColor = (mood) => {
  const group = MOOD_LOOKUP[normalizeMood(mood)];
  return group ? group.color : FALLBACK_COLOR;
};

export const getMoodScore = (mood) => {
  const group = MOOD_LOOKUP[normalizeMood(mood)];
  return group ? group.score : null;
};

// "a | b |  c " -> ['a', 'b', 'c'] (espaços internos múltiplos colapsados)
export const parseActivities = (activities) => {
  if (!activities) return [];
  return String(activities)
    .split('|')
    .map((token) => token.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
};

// "Energy: 5/10 points | Productivity: 8/10 points" -> [{name, value, max}]
export const parseScales = (scales) => {
  if (!scales) return [];
  return String(scales)
    .split('|')
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => {
      const match = token.match(/^(.*?):\s*(-?\d+(?:\.\d+)?)\s*\/\s*(\d+)/);
      if (!match) return null;
      return { name: match[1].trim(), value: Number(match[2]), max: Number(match[3]) };
    })
    .filter(Boolean);
};

export const averageMoodScore = (entries) => {
  const scores = entries.map((e) => getMoodScore(e.mood)).filter((s) => s !== null);
  if (scores.length === 0) return null;
  return scores.reduce((sum, s) => sum + s, 0) / scores.length;
};

export const moodDistribution = (entries) => {
  const counts = new Map();
  entries.forEach((e) => {
    const mood = normalizeMood(e.mood);
    if (!mood) return;
    counts.set(mood, (counts.get(mood) || 0) + 1);
  });
  const known = MOOD_ORDER.filter((mood) => counts.has(mood));
  const extras = [...counts.keys()].filter((mood) => !MOOD_ORDER.includes(mood));
  return [...known, ...extras].map((mood) => ({
    mood,
    count: counts.get(mood),
    color: getMoodColor(mood),
  }));
};

export const moodOverTime = (entries) => {
  const buckets = new Map();
  entries.forEach((e) => {
    const score = getMoodScore(e.mood);
    if (score === null || !e.entry_date) return;
    const month = String(e.entry_date).slice(0, 7); // YYYY-MM
    if (!buckets.has(month)) buckets.set(month, { sum: 0, count: 0 });
    const bucket = buckets.get(month);
    bucket.sum += score;
    bucket.count += 1;
  });
  return [...buckets.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, { sum, count }]) => ({
      month,
      avg: sum / count,
      count,
    }));
};

export const activityFrequency = (entries, topN = null) => {
  const counts = new Map();
  entries.forEach((e) => {
    parseActivities(e.activities).forEach((activity) => {
      counts.set(activity, (counts.get(activity) || 0) + 1);
    });
  });
  const sorted = [...counts.entries()]
    .map(([activity, count]) => ({ activity, count }))
    .sort((a, b) => b.count - a.count || a.activity.localeCompare(b.activity));
  return topN ? sorted.slice(0, topN) : sorted;
};

export const scalesAverages = (entries) => {
  const buckets = new Map();
  entries.forEach((e) => {
    parseScales(e.scales).forEach(({ name, value, max }) => {
      if (!buckets.has(name)) buckets.set(name, { sum: 0, count: 0, max });
      const bucket = buckets.get(name);
      bucket.sum += value;
      bucket.count += 1;
    });
  });
  return [...buckets.entries()]
    .map(([name, { sum, count, max }]) => ({ name, avg: sum / count, count, max }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

// Compara humor nos dias COM vs SEM determinada atividade.
export const moodByActivity = (entries, activity) => {
  const target = activity.replace(/\s+/g, ' ').trim().toLowerCase();
  const withAct = [];
  const withoutAct = [];
  entries.forEach((e) => {
    const has = parseActivities(e.activities).some((a) => a.toLowerCase() === target);
    (has ? withAct : withoutAct).push(e);
  });
  return {
    activity,
    with: {
      count: withAct.length,
      avg: averageMoodScore(withAct),
      distribution: moodDistribution(withAct),
    },
    without: {
      count: withoutAct.length,
      avg: averageMoodScore(withoutAct),
      distribution: moodDistribution(withoutAct),
    },
  };
};

// Ranking de atividades por impacto no humor: média de humor nos dias com a
// atividade menos a média geral. Filtra por suporte mínimo (minCount).
export const activityImpact = (entries, { minCount = 5 } = {}) => {
  const overall = averageMoodScore(entries);
  if (overall === null) return [];

  const byActivity = new Map();
  entries.forEach((e) => {
    const score = getMoodScore(e.mood);
    if (score === null) return;
    new Set(parseActivities(e.activities).map((a) => a.toLowerCase())).forEach((activity) => {
      if (!byActivity.has(activity)) byActivity.set(activity, { sum: 0, count: 0 });
      const bucket = byActivity.get(activity);
      bucket.sum += score;
      bucket.count += 1;
    });
  });

  return [...byActivity.entries()]
    .filter(([, { count }]) => count >= minCount)
    .map(([activity, { sum, count }]) => {
      const avgWith = sum / count;
      return { activity, count, avgWith, delta: avgWith - overall };
    })
    .sort((a, b) => b.delta - a.delta);
};
